import { Money } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import VariantPriceTooltip from "Catalog/Edit/shared/components/VariantPriceTooltip"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import Table from "shared/components/Table"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IProduct, ISku } from "shared/hubrise/models"
import * as Sku from "shared/hubrise/utils/Sku"

import { FieldEnabled, FieldName, FieldPrice, FieldRef } from "./Styles"

interface SkuTableProps {
  product: IProduct
  // True if the user has ticked the checkbox but the product is not yet multi-sku.
  isMultiSku: boolean
}

const SkuTable = ({ product, isMultiSku }: SkuTableProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()
  const { editor, catalog } = useEditor()
  const { currency } = catalog
  const { catalogNavigation } = useCatalogNavigation()
  const hasVariants = catalog.data.variants.length > 0

  const $newRowNameRef = React.useRef<HTMLInputElement>(null)

  const handleChange =
    <TField extends keyof ISku>(sku: ISku, updateForNew: ((sku: ISku) => void) | undefined, field: TField) =>
    (value: ISku[TField]) =>
      updateForNew ? updateForNew({ ...sku, [field]: value }) : editor.sku.update(product.id, sku.id, field, value)

  const handleReorder = (newSkus: Array<ISku>): void => {
    editor.product.reorderSkus(product.id, newSkus)
  }

  const handleDelete = (sku: ISku): void => {
    editor.sku.delete(product.id, sku.id)
  }

  const canDelete = product.skus.length >= 2

  const getNew = (): ISku => {
    setTimeout(() => $newRowNameRef.current!.focus(), 0)
    return Sku.create(catalog)
  }

  const tryCreate = (sku: ISku): boolean => {
    if (sku.name.length > 0) {
      editor.sku.add(product, sku)
      return true
    } else {
      return false
    }
  }

  return (
    <Table
      testId="sku-table"
      items={product.skus}
      columns={[
        ...(isMultiSku
          ? [
              {
                header: t("product.skus.name"),
                cellRenderer: (sku: ISku, updateForNew?: (sku: ISku) => void) => (
                  <FieldName
                    ref={updateForNew ? $newRowNameRef : undefined}
                    initialValue={sku.name}
                    onChange={handleChange(sku, updateForNew, "name")}
                    debounce={updateForNew === undefined}
                    validate={(name) => {
                      if (name.length === 0) {
                        return t("validations.not_empty")
                      } else if (
                        product.skus
                          .filter((s) => s.id !== sku.id)
                          .map(({ name }) => name)
                          .includes(name)
                      ) {
                        return t("validations.must_be_unique")
                      }
                    }}
                    linethrough={!sku.restrictions.enabled}
                  />
                ),
              },
            ]
          : []),
        {
          header: t("product.skus.price"),
          cellRenderer: (sku, updateForNew) => (
            <FieldPrice
              initialValue={sku.price}
              onChange={(value) => handleChange(sku, updateForNew, "price")(value || Money.zero(currency))}
              debounce={updateForNew === undefined}
              validate={(value) => (value === null ? t("validations.not_empty") : undefined)}
              currency={currency}
            />
          ),
        },
        {
          header: t("product.skus.ref"),
          cellRenderer: (sku, updateForNew) => (
            <FieldRef
              initialValue={sku.ref}
              onChange={handleChange(sku, updateForNew, "ref")}
              debounce={updateForNew === undefined}
            />
          ),
        },
        {
          header: isMultiSku ? t("product.skus.enabled") : t("product.enabled"),
          style: "centered",
          cellRenderer: (sku, updateForNew) => (
            <FieldEnabled
              initialValue={sku.restrictions.enabled}
              onChange={(enabled) => editor.sku.enable(product.id, sku.id, enabled)}
              debounce={updateForNew === undefined}
            />
          ),
        },
        ...(hasVariants
          ? [
              {
                header: t("product.skus.variants"),
                cellRenderer: (sku: ISku) => (
                  <VariantPriceTooltip
                    catalog={catalog}
                    navigateToGrid={() => navigate(catalogNavigation.gridPath("sku", sku.id))}
                    priceOverrides={sku.priceOverrides}
                    restrictions={sku.restrictions}
                  />
                ),
              },
            ]
          : []),
      ]}
      onReorder={handleReorder}
      onDelete={canDelete ? handleDelete : undefined}
      droppableId="skus"
      addBehavior={isMultiSku ? { buttonLabel: t("product.skus.add"), getNew, tryCreate } : undefined}
    />
  )
}

export default SkuTable
